// Context that must survive the redirect round-trip to the PayFast payment
// gateway. Returning from PayFast is a full-page load, which resets the Redux
// store (including the currently-selected organisation). sessionStorage, by
// contrast, persists across the external navigation within the same tab, so we
// use it to remember which organisation the payment was for and where to send
// the user once they come back.

const ORG_ID_KEY = "pendingPaymentOrgId"
const RETURN_PATH_KEY = "pendingPaymentReturnPath"

export interface PendingPaymentReturn {
  /** Organisation the payment was started for, restored as the active org on return. */
  organizationId: string
  /** Where to send the user after a successful payment (e.g. "/wallet", "/dashboard"). */
  returnPath: string
}

/** Call right before redirecting the browser to PayFast. */
export function setPendingPaymentReturn({
  organizationId,
  returnPath,
}: PendingPaymentReturn): void {
  try {
    sessionStorage.setItem(ORG_ID_KEY, organizationId)
    sessionStorage.setItem(RETURN_PATH_KEY, returnPath)
  } catch {
    // sessionStorage unavailable (private mode / disabled) — non-fatal.
  }
}

/** Read the pending context without clearing it. Returns null if none was set. */
export function peekPendingPaymentReturn(): PendingPaymentReturn | null {
  try {
    const organizationId = sessionStorage.getItem(ORG_ID_KEY)
    if (!organizationId) return null
    return {
      organizationId,
      returnPath: sessionStorage.getItem(RETURN_PATH_KEY) || "/dashboard",
    }
  } catch {
    return null
  }
}

/** Clear the pending context once it has been handled. */
export function clearPendingPaymentReturn(): void {
  try {
    sessionStorage.removeItem(ORG_ID_KEY)
    sessionStorage.removeItem(RETURN_PATH_KEY)
  } catch {
    // non-fatal
  }
}
